/**
 * SEO Data Layer — Restore.Dental
 * 
 * Canonical source of truth for all page metadata, titles, descriptions, and structured data.
 * Every page on the site must source its SEO from this file for consistency.
 * 
 * HARD CONSTRAINTS:
 * - Titles: ≤ 65 characters (Google truncates at ~60-65)
 * - Descriptions: ≤ 160 characters (Google truncates at ~155-160)
 * - URLs: No trailing slashes, lowercase, hyphens
 */

// ============================================================================
// CONSTANTS & DEFAULTS
// ============================================================================

/**
 * Public-facing site URL (no trailing slash)
 * Resolved from environment variable with production fallback
 */
export const PUBLIC_SITE_URL = (
  process.env.REACT_APP_PUBLIC_SITE_URL || 'https://restore.dental'
).replace(/\/$/, '');

/**
 * Default Open Graph image (1200x630px recommended)
 * Used when page doesn't specify a custom OG image
 * TODO: Design and replace placeholder with branded OG image
 */
export const DEFAULT_OG_IMAGE = `${PUBLIC_SITE_URL}/og-image.jpg`;

/**
 * Brand logo for structured data (near-square preferred: 800x800px)
 * Used in Organization schema for Google Knowledge Panel
 * Must be PNG or JPG (not SVG)
 */
export const BRAND_LOGO_IMAGE = `${PUBLIC_SITE_URL}/logo-stacked.jpg`;

/**
 * Site-wide default metadata
 */
export const SITE_DEFAULTS = {
  siteName: 'Restore.Dental',
  tagline: 'Dr. Kartik Patel | West London',
  locale: 'en_GB',
  twitterHandle: '@restoredentaluk', // Update if you have Twitter
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Truncate string at last word boundary within max length
 * Appends ellipsis if truncated
 * 
 * @param {string} str - Text to truncate
 * @param {number} max - Maximum character count (default: 160)
 * @returns {string} Truncated text with ellipsis if needed
 */
export const truncateForMeta = (str, max = 160) => {
  if (!str || str.length <= max) return str;
  
  const truncated = str.slice(0, max);
  const lastSpace = truncated.lastIndexOf(' ');
  
  // If no space found, hard cut at max
  if (lastSpace === -1) return `${truncated}…`;
  
  return `${truncated.slice(0, lastSpace)}…`;
};

/**
 * Validate SEO object meets character constraints
 * Logs warnings in development if constraints violated
 */
export const validateSEO = (seoData, pageName) => {
  if (process.env.NODE_ENV === 'development') {
    if (seoData.title.length > 65) {
      console.warn(`⚠️ SEO: ${pageName} title is ${seoData.title.length} chars (max 65)`);
    }
    if (seoData.description.length > 160) {
      console.warn(`⚠️ SEO: ${pageName} description is ${seoData.description.length} chars (max 160)`);
    }
  }
  return seoData;
};

// ============================================================================
// PAGE SEO DATA
// ============================================================================

/**
 * Homepage SEO
 * Focus: Brand awareness, general dentistry, West London location
 */
export const homePageSEO = validateSEO({
  title: 'Best Dentist Acton, West London — 15+ Yrs | Restore Dental',
  description: 'Award-winning dentist in Acton. Invisalign, implants, cosmetic dentistry. 4.9★ rating. 0% finance. Book your consultation with Dr. Kartik Patel today.',
  path: '/',
  keywords: [
    'dentist acton',
    'west london dentist',
    'dr kartik patel',
    'restore dental',
    'invisalign acton',
    'dental implants london',
    'cosmetic dentist',
  ],
  image: DEFAULT_OG_IMAGE,
}, 'Homepage');

/**
 * Finance Page SEO
 * Focus: Affordability, payment plans, 0% interest
 */
export const financePageSEO = validateSEO({
  title: 'Dental Finance — 0% Interest Plans 3-60 Months | Restore Dental',
  description: 'Spread the cost with 0% interest-free dental finance. Payment plans from 3-60 months. Quick 2-min application. Borrow up to £30,000.',
  path: '/finance',
  keywords: [
    'dental finance',
    '0% dental finance',
    'payment plans dentist',
    'dental payment plan',
    'finance dental treatment',
    'spread cost dentist',
  ],
  image: DEFAULT_OG_IMAGE,
}, 'Finance');

/**
 * General Dentistry — Acton
 * Focus: Comprehensive care, checkups, hygiene, fillings
 */
export const dentistActonPageSEO = validateSEO({
  title: 'General Dentist Acton — NHS & Private Care | Restore Dental',
  description: 'Trusted general dentist in Acton. Checkups, hygiene, fillings, root canals. 15+ years experience. Same-day appointments. Book online today.',
  path: '/dentist-acton',
  keywords: [
    'general dentist acton',
    'dentist acton w3',
    'nhs dentist acton',
    'private dentist acton',
    'dental checkup acton',
    'family dentist acton',
  ],
  image: DEFAULT_OG_IMAGE,
}, 'Dentist Acton');

/**
 * Invisalign — Acton
 * Focus: Clear aligners, 150+ cases, digital workflow
 */
export const invisalignActonPageSEO = validateSEO({
  title: 'Invisalign Acton — 150+ Cases, 0% Finance | Restore Dental',
  description: 'Expert Invisalign provider in Acton. Clear aligners from £2,800. Digital smile design. Interest-free payment plans. Free consultation available.',
  path: '/invisalign-acton',
  keywords: [
    'invisalign acton',
    'clear aligners acton',
    'invisible braces acton',
    'teeth straightening acton',
    'invisalign cost london',
    'orthodontist acton',
  ],
  image: DEFAULT_OG_IMAGE,
}, 'Invisalign Acton');

/**
 * Dental Implants — Acton
 * Focus: Permanent solution, digital planning, bone grafting
 */
export const implantsActonPageSEO = validateSEO({
  title: 'Dental Implants Acton — Digital Workflow £2,295 | Restore',
  description: 'Premium dental implants in Acton. 3D-guided surgery. Same-day teeth possible. 10-year warranty. Flexible payment plans from £191/month.',
  path: '/implants-acton',
  keywords: [
    'dental implants acton',
    'teeth implants acton',
    'implant dentist acton',
    'dental implants cost london',
    'single tooth implant',
    'full mouth implants',
  ],
  image: DEFAULT_OG_IMAGE,
}, 'Implants Acton');

/**
 * Composite Bonding — Acton
 * Focus: Same-day smile fix, chipped teeth, gaps, no-prep veneers
 */
export const compositeBondingActonPageSEO = validateSEO({
  title: 'Composite Bonding Acton — Same-Day Smile Fix | Restore',
  description: 'Composite bonding in Acton from £250/tooth. Fix chips, gaps, discoloration in one visit. Pain-free, no drilling. Instant smile transformation.',
  path: '/composite-bonding-acton',
  keywords: [
    'composite bonding acton',
    'dental bonding acton',
    'composite veneers acton',
    'teeth bonding london',
    'fix chipped tooth',
    'edge bonding teeth',
  ],
  image: DEFAULT_OG_IMAGE,
}, 'Composite Bonding Acton');

/**
 * Cosmetic Dentistry — Acton
 * Focus: Smile makeovers, veneers, whitening, full transformations
 */
export const cosmeticDentistActonPageSEO = validateSEO({
  title: 'Cosmetic Dentist Acton — Smile Makeovers | Restore Dental',
  description: 'Award-winning cosmetic dentist in Acton. Veneers, whitening, bonding, full smile makeovers. Digital smile design. 0% finance available.',
  path: '/cosmetic-dentist-acton',
  keywords: [
    'cosmetic dentist acton',
    'smile makeover acton',
    'porcelain veneers acton',
    'teeth whitening acton',
    'cosmetic dentistry london',
    'hollywood smile',
  ],
  image: DEFAULT_OG_IMAGE,
}, 'Cosmetic Dentist Acton');

/**
 * Emergency Dentist — Acton
 * Focus: Same-day appointments, pain relief, broken teeth, urgent care
 */
export const emergencyDentistActonPageSEO = validateSEO({
  title: 'Emergency Dentist Acton — Same-Day Pain Relief | Restore',
  description: 'Emergency dental care in Acton. Same-day appointments. Broken teeth, severe pain, lost fillings. Open 6 days. Call now: 07787 814 064.',
  path: '/emergency-dentist-acton',
  keywords: [
    'emergency dentist acton',
    'emergency dental acton',
    'same day dentist acton',
    'toothache acton',
    'broken tooth acton',
    'urgent dental care',
  ],
  image: DEFAULT_OG_IMAGE,
}, 'Emergency Dentist Acton');

// ============================================================================
// AUTHORITY / TRUST PAGES
// ============================================================================

export const aboutPageSEO = validateSEO({
  title: 'Dr Kartik Patel BDS — Dentist in Acton, GDC 177993 | Restore',
  description: 'Meet Dr Kartik Patel BDS (King\'s College London), principal dentist at Restore.Dental Acton. 15+ years, 150+ Invisalign cases, implants & cosmetic dentistry.',
  path: '/about-dr-kartik-patel',
  keywords: ['dr kartik patel', 'kartik patel dentist', 'dentist acton', 'invisalign dentist west london', 'gdc 177993', 'restore dental dentist'],
  image: '/dr-kartik-patel.jpg',
}, 'About');

export const pricingPageSEO = validateSEO({
  title: 'Dental Prices Acton — Invisalign, Implants, Bonding Fees | Restore',
  description: 'Transparent dental fee guide for Acton, West London. Invisalign from £2,800, implants from £2,295, bonding from £250/tooth. 0% finance 3-60 months.',
  path: '/pricing',
  keywords: ['dentist prices acton', 'invisalign cost london', 'dental implant cost london', 'composite bonding price', 'teeth whitening cost acton', 'dental fees west london'],
  image: DEFAULT_OG_IMAGE,
}, 'Pricing');

export const contactPageSEO = validateSEO({
  title: 'Contact Restore.Dental — 64a Horn Lane, Acton W3 6NP | Book Now',
  description: 'Restore.Dental, 64a Horn Lane, Acton, London W3 6NP. Call or WhatsApp 07787 814 064. 4 min from Acton Main Line (Elizabeth line). Open Mon–Sat.',
  path: '/contact',
  keywords: ['dentist horn lane acton', 'restore dental contact', 'dentist near acton main line', 'dentist w3 6np', 'book dentist acton'],
  image: DEFAULT_OG_IMAGE,
}, 'Contact');

export const notFoundPageSEO = {
  title: 'Page Not Found | Restore.Dental',
  description: 'Sorry, that page could not be found. Explore our dental treatments, prices and contact details.',
  path: '/404',
};

// ============================================================================
// AGGREGATE EXPORTS
// ============================================================================

/**
 * Map of all page SEO data by route path
 * Used for dynamic SEO rendering in components
 */
export const SEO_BY_PATH = {
  '/': homePageSEO,
  '/finance': financePageSEO,
  '/dentist-acton': dentistActonPageSEO,
  '/invisalign-acton': invisalignActonPageSEO,
  '/implants-acton': implantsActonPageSEO,
  '/composite-bonding-acton': compositeBondingActonPageSEO,
  '/cosmetic-dentist-acton': cosmeticDentistActonPageSEO,
  '/emergency-dentist-acton': emergencyDentistActonPageSEO,
  '/about-dr-kartik-patel': aboutPageSEO,
  '/pricing': pricingPageSEO,
  '/contact': contactPageSEO,
};

/**
 * Get SEO data for current path
 * Falls back to homepage SEO if path not found
 * 
 * @param {string} path - Current route path
 * @returns {object} SEO metadata object
 */
export const getSEOForPath = (path) => {
  return SEO_BY_PATH[path] || homePageSEO;
};

/**
 * Generate full URL for path
 * 
 * @param {string} path - Route path
 * @returns {string} Full canonical URL
 */
export const getCanonicalURL = (path) => {
  return `${PUBLIC_SITE_URL}${path}`;
};

/**
 * Generate Open Graph image URL
 * Falls back to default if not specified
 * 
 * @param {string|undefined} image - Custom OG image path
 * @returns {string} Full OG image URL
 */
export const getOGImage = (image) => {
  if (!image) return DEFAULT_OG_IMAGE;
  if (image.startsWith('http')) return image;
  return `${PUBLIC_SITE_URL}${image}`;
};

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

export default {
  PUBLIC_SITE_URL,
  DEFAULT_OG_IMAGE,
  BRAND_LOGO_IMAGE,
  SITE_DEFAULTS,
  homePageSEO,
  financePageSEO,
  dentistActonPageSEO,
  invisalignActonPageSEO,
  implantsActonPageSEO,
  compositeBondingActonPageSEO,
  cosmeticDentistActonPageSEO,
  emergencyDentistActonPageSEO,
  SEO_BY_PATH,
  getSEOForPath,
  getCanonicalURL,
  getOGImage,
  truncateForMeta,
  validateSEO,
};
